import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchEstablishments,
  fetchLocations,
  fetchLocationTypes,
} from '../api/hospitalApi';
import {
  filterEstablishments,
  getCountryOptionsFromLocations,
  getLocationTabsForParent,
  mapEstablishmentToCard,
} from '../utils/hospitalHelpers';

export function usePartnerHospitals({ noDefaults = false } = {}) {
  const [establishments, setEstablishments] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [activeLocation, setActiveLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    const [rows, locationRows, typeRows] = await Promise.all([
      fetchEstablishments(),
      fetchLocations(),
      fetchLocationTypes(),
    ]);
    setEstablishments(rows);
    setLocations(locationRows);
    setLocationTypes(typeRows);
    return { rows, locationRows, typeRows };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const { locationRows, typeRows } = await loadData();
        if (cancelled) return;

        const countryOptions = getCountryOptionsFromLocations(locationRows, typeRows);
        const defaultCountry = countryOptions.find((c) => c.name === 'India') || countryOptions[0] || null;
        setSelectedCountry(defaultCountry);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load hospitals.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [loadData]);

  const countries = useMemo(
    () => getCountryOptionsFromLocations(locations, locationTypes),
    [locations, locationTypes],
  );

  const locationTabs = useMemo(
    () => (
      selectedCountry
        ? getLocationTabsForParent(locations, selectedCountry.id, locationTypes)
        : []
    ),
    [locations, selectedCountry, locationTypes],
  );

  useEffect(() => {
    setActiveLocation(locationTabs[0] || null);
  }, [locationTabs]);

  const filteredEstablishments = useMemo(() => (
    filterEstablishments(establishments, {
      locationTab: activeLocation,
      country: selectedCountry,
    })
  ), [establishments, activeLocation, selectedCountry]);

  const filteredHospitals = useMemo(() => (
    filteredEstablishments.map((est) => mapEstablishmentToCard(est, { noDefaults }))
  ), [filteredEstablishments, noDefaults]);

  const handleCountrySelect = (countryId) => {
    const country = countries.find((c) => String(c.id) === String(countryId)) || null;
    setSelectedCountry(country);
  };

  const refreshEstablishments = useCallback(async () => {
    try {
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to refresh hospitals.');
    }
  }, [loadData]);

  return {
    countries,
    locationTabs,
    selectedCountry,
    activeLocation,
    setActiveLocation,
    handleCountrySelect,
    filteredHospitals,
    filteredEstablishments,
    refreshEstablishments,
    loading,
    error,
  };
}
